import { useCallback, useMemo } from "react";
import type { ThreadStepPlan } from "../../../../api/threads/threadsApi";
import { readEntries } from "../../../../api/threads/threadsApi";
import type { ProjectedStep, ThreadEntry } from "../../../../api/threads/liveMessages";
import { useLoad } from "../../../../shared/loading/useLoad";
import type { LoadedProcess } from "../../../workflows/data/processCatalogue";
import { stepFocus, type StepFocus } from "./stepFocus";
import { stepsInWorkflow, type StepInWorkflow } from "./stepUses";

export interface StepFocusView extends StepFocus {
  /** The Workflow's steps by progress key; empty while the Workflow is unread. */
  readonly steps: ReadonlyMap<string, StepInWorkflow>;
  /** Each step's plan by progress key, as module B gives it (ADR-0032 D7); empty while unread. */
  readonly plans: ReadonlyMap<string, ThreadStepPlan>;
  /** The open person step's request: from the log held, else read by its number; `null` until read. */
  readonly waitingRequest: ThreadEntry | null;
  /** The project's bindings artifact the Workflow's roles resolved through; `null` without one. */
  readonly bindings: string | null;
}

interface StepFocusInput {
  readonly threadId: string;
  readonly process: LoadedProcess | null;
  /** The Workflow's step keys, in order. */
  readonly order: readonly string[];
  /** The step the Run is at, as module C publishes it (W-22). */
  readonly current: ProjectedStep | null;
  readonly entries: readonly ThreadEntry[];
  readonly openHumanStepEntry: number | null;
  readonly plans: ReadonlyMap<string, ThreadStepPlan>;
}

const NO_STEPS: ReadonlyMap<string, StepInWorkflow> = new Map();

/** The current step, every step of the Workflow and its plan, from module C's projection and module B's step plans. */
export function useStepFocus({ threadId, process, order, current, entries, openHumanStepEntry, plans }: StepFocusInput): StepFocusView {
  const steps = useMemo(() => (process === null ? NO_STEPS : stepsInWorkflow(process)), [process]);
  const waitingRequest = useWaitingRequest(threadId, entries, openHumanStepEntry);
  return useMemo(() => {
    // Module B names a reason a value does not fit: Run waits, though C says Ready.
    const focus = stepFocus({ order, current, planLacksInput: (stepKey) => (plans.get(stepKey)?.reasons.length ?? 0) > 0 });
    return { ...focus, steps, plans, waitingRequest, bindings: process?.catalogue.bindings ?? null };
  }, [order, current, waitingRequest, steps, plans, process]);
}

/** The waiting request's entry by its exact number: the log held has it, or one read of that entry. */
function useWaitingRequest(threadId: string, entries: readonly ThreadEntry[], number: number | null): ThreadEntry | null {
  const held = useMemo(() => (number === null ? null : (entries.find((entry) => entry.number === number) ?? null)), [entries, number]);
  const load = useCallback(
    async (signal: AbortSignal) => (number === null || held !== null ? null : ((await readEntries(threadId, number - 1, 1, signal)).entries.find((entry) => entry.number === number) ?? null)),
    [threadId, number, held],
  );
  const read = useLoad(load).state;
  const found = held ?? (read.status === "loaded" ? read.value : null);
  return found?.payload.kind === "human_step_requested" ? found : null;
}
